
export type Point2D = [number, number];
export type Point3D = [number, number, number];
//export const PROXIMITY: number = 0.0000000001; // 1E-10; i.e. an atom
export const PROXIMITY: number = 0.0001; 
export enum Axis { X = 0, Y = 1, Z = 2 }
export enum Dimensions { D2 = 2, D3 = 3 }

/**
 * Returns an array of indices ordered by the angle of their corresponding
 * point. The points are ordered by their angle with the given central point in
 * the given direction. The array starts with the point closest to the angle the
 * start point makes with the central point. The aray ends in a similar fashion
 * at the end point. When the endpoint is omitted, the search runs a full 360
 * degrees
 * 
 * @param points
 *            array of points which are arrays of length 2 themselves : [
 *            [x0,y0], [x1,y1], ... [xn,yn] ]
 * @param centralPoint
 *            the point to use for angle calculations. Can be any arbitrary
 *            point or an index pointing to a point in the given list
 * @param startPoint
 *            the point that determines the start of the ordered list. Can be
 *            any arbitrary point or an index pointing to a point in the given
 *            list
 * @param endPoint
 *            (optional) the point that determines the end of the ordered list.
 *            Can be any arbitrary point or an index pointing to a point in the
 *            given list
 * @param direction
 *            the sorting direction. 0 = clockwise, 1 = counter clockwise
 * @return an array containing the ordered indices of the given points. When the
 *         given central point is an index, this index will not appear in the
 *         returned array.
 */
export const orderByAngle = (points: Point2D[], centralPoint: Point2D | number, startPoint: Point2D | number, direction: number, endPoint?: Point2D | number): number[] => {
	const cp = (<Point2D>centralPoint).push ? <Point2D>centralPoint : points[(<number>centralPoint)];
	const cpi = (<Point2D>centralPoint).push ? -3 : <number>centralPoint;

	const _endPoint = endPoint ? endPoint : startPoint;

	const spi = (<Point2D>startPoint).push ? -1 : <number>startPoint;
	const markerArray = (<Point2D>startPoint).push ? [<Point2D>startPoint] : [];

	const epi = (<Point2D>_endPoint).push ? spi === -1 ? -2 : -1 : <number>_endPoint;
	if ((<Point2D>_endPoint).push) markerArray.splice(0, 0, <Point2D>_endPoint)

	interface IndexPoint { i: number, p: Point2D };

	const dir = [
		(ip0: IndexPoint, ip1: IndexPoint) => {
			// negative means travelling from P0 to P1 is in CW direction
			return (ip0.p[0] - cp[0]) * (cp[0], ip1.p[1] - cp[1]) - (ip1.p[0] - cp[0]) * (ip0.p[1] - cp[1]);
		},
		(ip0: IndexPoint, ip1: IndexPoint) => {
			// negative means travelling from P0 to P1 is in CCW direction
			return (ip1.p[0] - cp[0]) * (ip0.p[1] - cp[1]) - (ip0.p[0] - cp[0]) * (ip1.p[1] - cp[1]);
		}
	];

	const pos = (ip0: IndexPoint, ip1: IndexPoint) => {
		const dP0 = [ip0.p[0] - cp[0], ip0.p[1] - cp[1]];
		const dP1 = [ip1.p[0] - cp[0], ip1.p[1] - cp[1]];
		return dP0[0] === dP1[0] ? dP1[1] - dP0[1] : dP1[0] - dP0[0];
	};

	let sortedPoints = markerArray.concat(points)
		.map((p, i) => { return { i: i - markerArray.length, p: p }; })
		.filter(ip => ip.i !== cpi) // filter out the given centre point
		.sort(pos) // sort points by position
		.sort(dir[direction]) // sort points by angle
		.map(ip => ip.i)// keep the indices

	const startMarkerIndex = sortedPoints.indexOf(spi);
	const endMarkerIndex = sortedPoints.indexOf(epi);
	if (startMarkerIndex < endMarkerIndex) {
		sortedPoints = sortedPoints.slice(startMarkerIndex, endMarkerIndex + 1);
	} else if (startMarkerIndex > endMarkerIndex) {
		sortedPoints = sortedPoints.slice(startMarkerIndex).concat(sortedPoints.slice(0, endMarkerIndex + 1));
	} else {
		sortedPoints = sortedPoints.slice(startMarkerIndex).concat(sortedPoints.slice(0, endMarkerIndex));
	}
	sortedPoints = spi < 0 ? sortedPoints.slice(1) : sortedPoints;
	sortedPoints = epi < 0 ? sortedPoints.slice(0, sortedPoints.length - 1) : sortedPoints;

	return sortedPoints;
}
export enum CornerType {
	TOP_LEFT = 0,
	TOP_RIGHT = 1,
	BOTTOM_RIGHT = 2,
	BOTTOM_LEFT = 3
}
/**
 * Returns the index of the point that lies closest to the requested corner.
 * 
 * @param points
 *            array of points which are arrays of length 2 themselves : [
 *            [x0,y0], [x1,y1], ... [xn,yn] ]
 * @param cornerType
 *            the corner to find amongst the given points. 0 = top-left, 1 =
 *            top-right, 2 = bottom-right, 3 = bottom-left
 * @param vThenH
 *            search direction. When set to true (default) the order is vertical
 *            then horizontal. When false it is horizontal then vertical
 * @return the index of the point at the corner
 */
export const corner = (points: Point2D[], cornerType: CornerType, vThenH: boolean = true): number => {
	const cornerFinders = [
		(p0: Point2D, p1: Point2D): number => {
			const top = p1[1] - p0[1];
			return top === 0 ? p0[0] - p1[0] : top;
		},
		(p0: Point2D, p1: Point2D): number => {
			const top = p1[1] - p0[1];
			return top === 0 ? p1[0] - p0[0] : top;
		},
		(p0: Point2D, p1: Point2D): number => {
			const bottom = p0[1] - p1[1];
			return bottom === 0 ? p1[0] - p0[0] : bottom;
		},
		(p0: Point2D, p1: Point2D): number => {
			const bottom = p0[1] - p1[1];
			return bottom === 0 ? p0[0] - p1[0] : bottom;
		},
		(p0: Point2D, p1: Point2D): number => {
			const left = p0[0] - p1[0];
			return left === 0 ? p1[1] - p0[1] : left;
		},
		(p0: Point2D, p1: Point2D): number => {
			const right = p1[0] - p0[0];
			return right === 0 ? p1[1] - p0[1] : right;
		},
		(p0: Point2D, p1: Point2D): number => {
			const right = p1[0] - p0[0];
			return right === 0 ? p0[1] - p1[1] : right;
		},
		(p0: Point2D, p1: Point2D): number => {
			const left = p0[0] - p1[0];
			return left === 0 ? p0[1] - p1[1] : left;
		}
	];

	const cti = cornerType + (vThenH ? 0 : 4);
	return points.reduce((ci, p, i, ps) => (ci === undefined) ? i : cornerFinders[cti](ps[ci], p) < 0 ? ci : i, <number>undefined);
}


/**
 * Check if two floating numbers are close
 */
export const closeNumber = (f0: number, f1: number, proximity = PROXIMITY): boolean => Math.abs(f0 - f1) <= proximity;
/**
 * Check if two points are close
 */
export const closePoints = (p0: Point2D, p1: Point2D, proximity = PROXIMITY): boolean => closeNumber(p0[0], p1[0], proximity) && closeNumber(p0[1], p1[1], proximity);
/**
 * Check if two points are exactly the same
 */
export const eqPoint = (p0: Point2D, p1: Point2D): boolean => p0[0] === p1[0] && p0[1] === p1[1];
/**
 * returns true if p0 has a higher value for the given axis then p1. When these values are
 * within proximity of each other, the test is performed on the other axis. The default 
 * axis is Axis.Y
 * @param p0  the point that must be higher in order to return true
 * @param p1  the point that must be lower in order to return true
 * @param axis the axis where to test for. Defaults to Axis.Y
 */
export const greaterAxisValueThan = (p0: Point2D, p1: Point2D, axis: Axis = Axis.Y): boolean => {
	if (p0[<number>axis] > p1[<number>axis] + PROXIMITY) {
		return true;
	} else if (p0[<number>axis] < p1[<number>axis] - PROXIMITY) {
		return false;
	} else {
		return (p0[1 - axis] > p1[1 - axis]);
	}
}
/**
 * returns true if p0 has a higher value for the given axis then p1. When these values are
 * within proximity of each other, the test is performed on the other axis which also 
 * returns true when these values are equal. The default 
 * axis is Axis.Y
 * TODO check this implementation if seidel build fails because it seems dodgy
 * @param p0  the point that must be higher in order to return true
 * @param p1  the point that must be lower in order to return true
 * @param axis the axis where to test for. Defaults to Axis.Y
 */
export const greaterOrEqualAxisValueThan = (p0: Point2D, p1: Point2D, axis: Axis = Axis.Y): boolean => {
	if (p0[<number>axis] > p1[<number>axis] + PROXIMITY) {
		return true;
	} else if (p0[<number>axis] < p1[<number>axis] - PROXIMITY) {
		return false;
	} else {
		return (p0[1 - axis] >= p1[1 - axis]);
	}
}
/**
 * Compares two points along the given axis. If the points lie within the given proximity for given axis,
 * the other axis is tested. If the points lie within the given proximity for the other axis as well then
 * 0 gets returned and the points lie on top of each other. When negative then point0 comes before point1.
 * When positive then point1 comes before point0 
 * @param p0 point to compare
 * @param p1 other point to compare
 * @param axis the axis to use first during the comparison. Defaults to the Y-axis
 * @param proximity the distance considered as zero distance. Defaults to PROXIMITY (1E-10; i.e. an atom);
 */
export const comparePoint2D = (p0: Point2D, p1: Point2D, axis: Axis = Axis.Y, proximity = PROXIMITY): number => {
	const axisDiff = p0[<number>axis] - p1[<number>axis];
	if (Math.abs(axisDiff) <= proximity) {
		const otherAxisDiff = p0[1 - <number>axis] - p1[1 - <number>axis];
		if (Math.abs(otherAxisDiff) <= proximity) {
			return 0;
		} else {
			return otherAxisDiff;
		}
	} else {
		return axisDiff;
	}
}
/**
 * returns true if p0 has a lower value for the given axis then p1. When these values are
 * within proximity of each other, the test is performed on the other axis. The default 
 * axis is Axis.Y
 * @param p0  the point that must be lower in order to return true
 * @param p1  the point that must be higher in order to return true
 * @param axis the axis where to test for. Defaults to Axis.Y
 */
export const lesserAxisValueThan = (p0: Point2D, p1: Point2D, axis: Axis = Axis.Y): boolean => {
	if (p0[<number>axis] < p1[<number>axis] - PROXIMITY) {
		return true;
	} else if (p0[<number>axis] > p1[<number>axis] + PROXIMITY) {
		return false;
	} else {
		return (p0[1 - axis] < p1[1 - axis]);
	}
}
/**
 * returns true if p0 has a lower value for the given axis then p1. When these values are
 * within proximity of each other, the test is performed on the other axis which also 
 * returns true when these values are equal. The default 
 * axis is Axis.Y
 * TODO check this implementation if seidel build fails because it seems dodgy
 * @param p0  the point that must be lower in order to return true
 * @param p1  the point that must be higher in order to return true
 * @param axis the axis where to test for. Defaults to Axis.Y
 */
export const lesserOrEqualAxisValueThan = (p0: Point2D, p1: Point2D, axis: Axis = Axis.Y): boolean => {
	if (p0[<number>axis] < p1[<number>axis] - PROXIMITY) {
		return true;
	} else if (p0[<number>axis] > p1[<number>axis] + PROXIMITY) {
		return false;
	} else {
		return (p0[1 - axis] <= p1[1 - axis]);
	}
}
/**
 * returns the point with the higher value for the given axis. When these values are
 * within proximity of each other, the test is performed on the other axis. The default 
 * axis is Axis.Y
 * @param p0  the point to be tested
 * @param p1  the other point to be tested
 * @param axis the axis where to test for. Defaults to Axis.Y
 * @return 0 for p0 and 1 for p1
 */
export const maxPointIndex = (p0: Point2D, p1: Point2D, axis: Axis = Axis.Y): number => {
	if (p0[<number>axis] > p1[<number>axis] + PROXIMITY) {
		return 0;
	} else if (Math.abs(p0[<number>axis] - p1[<number>axis]) <= PROXIMITY) {
		return p0[1 - axis] > p1[1 - axis] + PROXIMITY ? 0 : 1;
	} else {
		return 1;
	}
}
/**
 * returns the point with the lower value for the given axis. When these values are
 * within proximity of each other, the test is performed on the other axis. The default 
 * axis is Axis.Y
 * @param p0  the point to be tested
 * @param p1  the other point to be tested
 * @param axis the axis where to test for. Defaults to Axis.Y
 * @return 0 for p0 and 1 for p1
 */
export const minPointIndex = (p0: Point2D, p1: Point2D, axis: Axis = Axis.Y): number => {
	if (p0[<number>axis] < p1[<number>axis] - PROXIMITY) {
		return 0;
	} else if (Math.abs(p0[<number>axis] - p1[<number>axis]) <= PROXIMITY) {
		return p0[1 - axis] < p1[1 - axis] - PROXIMITY ? 0 : 1;
	} else {
		return 1;
	}
}
/**
 * returns the point with the higher value for the given axis. When these values are
 * within proximity of each other, the test is performed on the other axis. The default 
 * axis is Axis.Y
 * @param p0  the point to be tested
 * @param p1  the other point to be tested
 * @param axis the axis where to test for. Defaults to Axis.Y
 */
export const maxPoint = (p0: Point2D, p1: Point2D, axis: Axis = Axis.Y): Point2D => {
	if (p0[<number>axis] > p1[<number>axis] + PROXIMITY) {
		return p0;
	} else if (Math.abs(p0[<number>axis] - p1[<number>axis]) <= PROXIMITY) {
		return p0[1 - axis] > p1[1 - axis] + PROXIMITY ? p0 : p1;
	} else {
		return p1;
	}
}
/**
 * returns the point with the lower value for the given axis. When these values are
 * within proximity of each other, the test is performed on the other axis. The default 
 * axis is Axis.Y
 * @param p0  the point to be tested
 * @param p1  the other point to be tested
 * @param axis the axis where to test for. Defaults to Axis.Y
 */
export const minPoint = (p0: Point2D, p1: Point2D, axis: Axis = Axis.Y): Point2D => {
	if (p0[<number>axis] < p1[<number>axis] - PROXIMITY) {
		return p0;
	} else if (Math.abs(p0[<number>axis] - p1[<number>axis]) <= PROXIMITY) {
		return p0[1 - axis] < p1[1 - axis] - PROXIMITY ? p0 : p1;
	} else {
		return p1;
	}
}

/**
 * calculates the weighted average of given points
 */
export const average = (points: Point2D[]) => {
	return points.reduce((c, p, i, ps) => {
		c[0] += p[0] / ps.length;
		c[1] += p[1] / ps.length;
		return c;
	}, [0, 0]);
}
/**
 * return the length of 2D vector
 */
export const length2D = (v: Point2D): number => Math.sqrt(v[0] * v[0] + v[1] * v[1]);
/**
 * return the length of 2D vector
 */
export const length3D = (v: Point3D): number => Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
/**
 * Returns the dot product (scalar product) of two 2D vectors v0 and v1.
 * With this value you can calculate the COS of the angle that these 
 * vectors make:
 * 
 * cos(alpha) = dot2D(v0, v1) / ( length2D(v0) * length2D(v1) )
 */
export const dot2D = (v0: Point2D, v1: Point2D): number => (v0[0] * v1[0] + v0[1] * v1[1]);
/**
 * Returns the dot product (scalar product) of two 2D vectors v0 and v1
 */
export const dot3D = (v0: Point3D, v1: Point3D): number => (v0[0] * v1[0] + v0[1] * v1[1] + v0[2] * v1[2]);
/**
 * Returns the cross product of vector 0 and vector 1
 * @param v0 3D vector 0
 * @param v1 3D vector 1
 * @return the cross product of vector 0 and vector 1
 */
export const cross = (v0: Point3D, v1: Point3D): Point3D => [v0[1] * v1[2] - v0[2] * v1[1], v0[2] * v1[0] - v0[0] * v1[2], v0[0] * v1[1] - v0[1] * v1[0]];
/**
 * Returns the Z coordinate of the cross product of two vectors who's Z 
 * coordinate is zero. i.e. two 2D vectors. The X and Y coordinates of
 * this cross product are zero. With this value you can calculate
 * the SIN of the angle that these vectors make:
 * 
 * sin(alpha) = crossZ(v0, v1) / ( length2D(v0) * length2D(v1) )
 *  
 * @param v0 2D vector 0
 * @param v1 2D vector 1
 * @return the Z coordinate of the cross product.
 */
export const crossZ = (v0: Point2D, v1: Point2D): number => v0[0] * v1[1] - v0[1] * v1[0];
/**
 * return the area size multiplied by 2 of a triangle defined by the given
 * points. When the triangle is CCW then the area is positive, otherwise
 * negative. When the three points are collinear then 0 is returned. This
 * formula is the derived from the cross product where the Z coordinates 
 * are zero.
 */
export const area2 = (v0: Point2D, v1: Point2D, v2: Point2D): number => (v1[0] - v0[0]) * (v2[1] - v0[1]) - (v2[0] - v0[0]) * (v1[1] - v0[1]);
/**
 * Check if three points are ordered ccw (>0), collinear (=0) or cw(<0)
 */
export const orientation = area2;
/**
 * Check if three points are CCW (counter clock wise)
 */
export const isCCW = (p0: Point2D, p1: Point2D, p2: Point2D): boolean => orientation(p0, p1, p2) > 0;
/**
 * Check if three points are CW (clock wise)
 */
export const isCW = (p0: Point2D, p1: Point2D, p2: Point2D): boolean => orientation(p0, p1, p2) < 0;
/**
 * Check if three points are collinear
 */
export const isCollinear = (p0: Point2D, p1: Point2D, p2: Point2D): boolean => orientation(p0, p1, p2) === 0;

/**
 * Checks if point p lies on inside the rectangle defined by points lp0 and lp1.
 * N.B. if the three points are collinear then point p lies on the line defined
 * by points lp0 and lp1
 */
export const between = (lp0: Point2D, lp1: Point2D, p: Point2D): boolean => p[0] <= Math.max(lp0[0], lp1[0]) && p[0] >= Math.min(lp0[0], lp1[0]) && p[1] <= Math.max(lp0[1], lp1[1]) && p[1] >= Math.min(lp0[1], lp1[1]);

/**
 * Check of two lines intersect
 */
export const intersect = (l0p0: Point2D, l0p1: Point2D, l1p0: Point2D, l1p1: Point2D): boolean => {

	// >0 l1p0 lies left from line between l0p0 and l0p1, <0 means right
	// from the line and 0 means on the line
	const o0 = Math.sign(orientation(l0p0, l0p1, l1p0));
	const o1 = Math.sign(orientation(l0p0, l0p1, l1p1));
	const o2 = Math.sign(orientation(l1p0, l1p1, l0p0));
	const o3 = Math.sign(orientation(l1p0, l1p1, l0p1));

	if (o0 * o1 < 0 && o2 * o3 < 0) {
		return true
	} else if (o0 === 0 && between(l0p0, l0p1, l1p0)) {
		return true;
	} else if (o1 === 0 && between(l0p0, l0p1, l1p1)) {
		return true;
	} else if (o2 === 0 && between(l1p0, l1p1, l0p0)) {
		return true;
	} else if (o3 === 0 && between(l1p0, l1p1, l0p1)) {
		return true;
	} else {
		return false;
	}
}

export enum IntersectionType {
	NONE, //no intersection
	NORMAL, //normal intersection
	VERTEX,// a vertex of one segment lies on the other segment
	VERTEX_DOUBLE, //the segments share a common vertex 
	OVERLAP// the segments are colinear and overlap each other
}
export interface IntersectionResult {
	type: IntersectionType,
	i0: Point2D,
	i1: Point2D,
	t0: number,
	t1: number
}
const overlap = (l0p0: Point2D, l0p1: Point2D, l1p0: Point2D, l1p1: Point2D): IntersectionResult => {
	let intersectionResult: IntersectionResult = {
		type: IntersectionType.NONE,
		i0: undefined,
		i1: undefined,
		t0: undefined,
		t1: undefined
	};
	if (isCollinear(l0p0, l0p1, l1p0)) {
		if (isCollinearPointOnSegment(l0p0, l0p1, l1p0)) {
			//first vertex of segment 1 lies on segment 0 
			intersectionResult.type = IntersectionType.OVERLAP;
			intersectionResult.t0 = getTForCollinearPointOnSegment(l0p0, l0p1, l1p0);
			intersectionResult.i0 = l1p0;
			if (isCollinearPointOnSegment(l0p0, l0p1, l1p1)) {
				//last vertex of segment 1 lies on segment 0 -> segment1  lies entirely on segment 0  
				intersectionResult.t1 = getTForCollinearPointOnSegment(l0p0, l0p1, l1p1);
				intersectionResult.i1 = l1p1;
			} else {
				//last vertex of segment 1 lies outside segment 0  
				if (isCollinearPointOnSegment(l1p0, l1p1, l0p1)) {
					//last vertex of segment 0 lies on segment 1 
					intersectionResult.t1 = getTForCollinearPointOnSegment(l1p0, l1p1, l0p1);
					intersectionResult.i1 = l0p1;
				} else {
					//this case should not occur
					//first vertex of segment 0 lies on segment 1 
					intersectionResult.t1 = getTForCollinearPointOnSegment(l1p0, l1p1, l0p0);
					intersectionResult.i1 = l0p0;
				}
			}
		} else if (isCollinearPointOnSegment(l1p0, l1p1, l0p0)) {
			//first vertex of segment 0 lies on segment 1 
			intersectionResult.type = IntersectionType.OVERLAP;
			intersectionResult.t1 = getTForCollinearPointOnSegment(l1p0, l1p1, l0p0);
			intersectionResult.i1 = l0p0;
			if (isCollinearPointOnSegment(l1p0, l1p1, l0p1)) {
				//last vertex of segment 0 lies on segment 1 -> segment0  lies entirely on segment 1  
				intersectionResult.t0 = getTForCollinearPointOnSegment(l1p0, l1p1, l0p1);
				intersectionResult.i0 = l0p1;
			} else {

				//last vertex of segment 0 lies outside segment 1  
				if (isCollinearPointOnSegment(l0p0, l0p1, l1p1)) {
					//last vertex of segment 1 lies on segment 0 
					intersectionResult.t0 = getTForCollinearPointOnSegment(l0p0, l0p1, l1p1);
					intersectionResult.i0 = l1p1;
				} else {
					//this case should not occur
					//first vertex of segment 1 lies on segment 0 
					intersectionResult.t0 = getTForCollinearPointOnSegment(l0p0, l0p1, l1p0);
					intersectionResult.i0 = l1p0;
				}
			}
		} else {
			//colinear but no overlap
		}
	}
	return intersectionResult;
}

const isCollinearPointOnSegment = (lp0: Point2D, lp1: Point2D, p: Point2D): boolean => {
	return lp0[0] === lp1[0] ?
		p[1] <= Math.max(lp0[1], lp1[1]) && p[1] >= Math.min(lp0[1], lp1[1]) :
		p[0] <= Math.max(lp0[0], lp1[0]) && p[0] >= Math.min(lp0[0], lp1[0]);
}
const getTForCollinearPointOnSegment = (lp0: Point2D, lp1: Point2D, p: Point2D): number => {
	if (Math.abs(lp0[0] - lp1[0]) > Math.abs(lp0[1] - lp1[1])) {
		return (p[0] - lp0[0]) / (lp1[0] - lp0[0])
	} else {
		return (p[1] - lp0[1]) / (lp1[1] - lp0[1])
	}
}
export const pointOnSegment = (lp0: Point2D, lp1: Point2D, p: Point2D): boolean => {
	if (isCollinear(lp0, lp1, p)) {
		return isCollinearPointOnSegment(lp0, lp1, p);
	}
	return false;
}
export const getSegmentEndPointIndex = (lp0: Point2D, lp1: Point2D, p: Point2D): number => {
	return eqPoint(lp0,p) ? 0 : eqPoint(lp1,p) ? 1 : -1; 
}
export const intersection = (l0p0: Point2D, l0p1: Point2D, l1p0: Point2D, l1p1: Point2D): IntersectionResult => {
	const denomenator =
		l0p0[0] * (l1p1[1] - l1p0[1]) +
		l0p1[0] * (l1p0[1] - l1p1[1]) +
		l1p1[0] * (l0p1[1] - l0p0[1]) +
		l1p0[0] * (l0p0[1] - l0p1[1]);

	if (denomenator === 0) {
		return overlap(l0p0, l0p1, l1p0, l1p1);
	} else {
		let intersectionResult: IntersectionResult = {
			type: IntersectionType.NONE,
			i0: undefined,
			i1: undefined,
			t0: undefined,
			t1: undefined
		};
		let numerator =
			l0p0[0] * (l1p1[1] - l1p0[1]) +
			l1p0[0] * (l0p0[1] - l1p1[1]) +
			l1p1[0] * (l1p0[1] - l0p0[1]);
		if (closeNumber(numerator, 0)) {
			intersectionResult.t0 = 0;
			intersectionResult.type = IntersectionType.VERTEX;
		} else if (closeNumber(numerator, denomenator)) {
			intersectionResult.t0 = 1;
			intersectionResult.type = IntersectionType.VERTEX;
		} else {
			intersectionResult.t0 = numerator / denomenator;
		}

		numerator =
			-(l0p0[0] * (l1p0[1] - l0p1[1]) +
				l0p1[0] * (l0p0[1] - l1p0[1]) +
				l1p0[0] * (l0p1[1] - l0p0[1]));
		if (closeNumber(numerator, 0)) {
			intersectionResult.t1 = 0;
			intersectionResult.type = intersectionResult.type === IntersectionType.VERTEX ? IntersectionType.VERTEX_DOUBLE : IntersectionType.VERTEX;
		} else if (closeNumber(numerator, denomenator)) {
			intersectionResult.t1 = 1;
			intersectionResult.type = intersectionResult.type === IntersectionType.VERTEX ? IntersectionType.VERTEX_DOUBLE : IntersectionType.VERTEX;
		} else {
			intersectionResult.t1 = numerator / denomenator;
		}

		intersectionResult.i0 = [l0p0[0] + intersectionResult.t0 * (l0p1[0] - l0p0[0]),
		l0p0[1] + intersectionResult.t0 * (l0p1[1] - l0p0[1])];

		intersectionResult.i1 = intersectionResult.i0;

		//        intersectionResult.i1 = [l1p0[0] + intersectionResult.t1 * ( l1p1[0] - l1p0[0] ),
		//        l1p0[1] + intersectionResult.t1 * ( l1p1[1] - l1p0[1] )];

		if (0 < intersectionResult.t0 &&
			intersectionResult.t0 < 1 &&
			0 < intersectionResult.t1 &&
			intersectionResult.t1 < 1) {
			intersectionResult.type = IntersectionType.NORMAL;
		} else if (0 > intersectionResult.t0 ||
			intersectionResult.t0 > 1 ||
			0 > intersectionResult.t1 ||
			intersectionResult.t1 > 1) {
			intersectionResult.type = IntersectionType.NONE;
		}

		return intersectionResult;

	}
}

/**
 * Checks if point p lies inside the circumcircle of vertices v0, v1 and v2
 */
export const inCircumcircle = (v0: Point2D, v1: Point2D, v2: Point2D, p: Point2D): boolean => {
	const v = isCCW(v0, v1, v2) ? [v0, v1, v2] : [v2, v1, v0];
	const a = v[0][0] - p[0];
	const b = v[0][1] - p[1];
	const c = a * a + b * b;
	const d = v[1][0] - p[0];
	const e = v[1][1] - p[1];
	const f = d * d + e * e;
	const g = v[2][0] - p[0];
	const h = v[2][1] - p[1];
	const i = g * g + h * h;
	return a * e * i + b * f * g + c * d * h - c * e * g - b * d * i - a * f * h > 0;
}
/**
 * Checks if the line from point a to point b lies inside the cone defined by
 * the points aP(redecessor, a and aS(uccessor)
 */
export const inCone = (aP: Point2D, a: Point2D, aS: Point2D, b: Point2D) => {
	const convex = orientation(a, aS, aP) >= 0;
	const o_aP = orientation(a, b, aP); // >0 means left
	const o_aS = orientation(a, b, aS); // >0 means left

	if (convex) {
		return o_aP > 0 && o_aS < 0;
	} else {
		return !(o_aS >= 0 && o_aP <= 0);
	}
}
/**
 * Returns the y coordinate for given x on the line running through point a and b
 * @param a point on the line
 * @param b other point on the line
 * @param x the  x where to calculate y for
 * @return the y-coordinate which is undefined for vertical lines
 */
export const y2D = (a: Point2D, b: Point2D, x: number): number => {
	return (a[0] !== b[0]) ?
		(a[1] + (x - a[0]) * (b[1] - a[1]) / (b[0] - a[0])) :
		undefined;
}
/**
 * Returns the x coordinate for given y on the line running through point a and b
 * @param a point on the line
 * @param b other point on the line
 * @param y the  y where to calculate x for
 * @return the x-coordinate which is undefined for horizontal lines
 */
export const x2D = (a: Point2D, b: Point2D, y: number): number => {
	return (a[1] !== b[1]) ?
		(a[0] + (y - a[1]) * (b[0] - a[0]) / (b[1] - a[1])) :
		undefined;
}
